import { createSelector } from '@ngrx/store';
import { groupsAdapter, accountsAdapter, initialState, RbacState } from './rbac.feature';

type RootState = { rbac?: RbacState } | undefined;

const selectRbacState = (state: RootState): RbacState => state?.rbac ?? initialState;

const { selectAll: selectAllGroups } = groupsAdapter.getSelectors(
  (state: RootState) => selectRbacState(state).groups,
);
const { selectAll: selectAllAccounts } = accountsAdapter.getSelectors(
  (state: RootState) => selectRbacState(state).accounts,
);

export { selectAllGroups, selectAllAccounts };

export const selectGroupsLoading = createSelector(selectRbacState, (s) => s.groupsLoading);
export const selectGroupsSaving = createSelector(selectRbacState, (s) => s.groupsSaving);
export const selectGroupsError = createSelector(selectRbacState, (s) => s.groupsError);
export const selectGroupsQuery = createSelector(selectRbacState, (s) => s.groupsQuery);
export const selectGroupsFilters = createSelector(selectRbacState, (s) => s.groupsFilters);

export const selectAccountsLoading = createSelector(selectRbacState, (s) => s.accountsLoading);
export const selectAccountsSaving = createSelector(selectRbacState, (s) => s.accountsSaving);
export const selectAccountsError = createSelector(selectRbacState, (s) => s.accountsError);
export const selectAccountsQuery = createSelector(selectRbacState, (s) => s.accountsQuery);
export const selectAccountsFilters = createSelector(selectRbacState, (s) => s.accountsFilters);
export const selectAccountsTotalElements = createSelector(
  selectRbacState,
  (s) => s.accountsTotalElements,
);
export const selectAccountsCurrentPage = createSelector(
  selectRbacState,
  (s) => s.accountsCurrentPage,
);
export const selectAccountsPageSize = createSelector(selectRbacState, (s) => s.accountsPageSize);

export const selectFeatureDetails = createSelector(selectRbacState, (s) => s.featureDetails);
export const selectFeatureDetailsLoading = createSelector(
  selectRbacState,
  (s) => s.featureDetailsLoading,
);

export const selectPermissions = createSelector(selectRbacState, (s) => s.permissions);
export const selectPermissionsLoading = createSelector(
  selectRbacState,
  (s) => s.permissionsLoading,
);
export const selectPermissionsByAccount = (accountUuid: string) =>
  createSelector(selectRbacState, (s) =>
    s.permissions.filter((p) => p.accountUuid === accountUuid),
  );

export const selectInvitations = createSelector(selectRbacState, (s) => s.invitations);
export const selectInvitationsLoading = createSelector(
  selectRbacState,
  (s) => s.invitationsLoading,
);

export const selectCurrentTenantUuid = createSelector(selectRbacState, (s) => s.currentTenantUuid);

const normalize = (v: string | null | undefined): string => {
  if (!v) return '';
  return v
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
};

export const selectFilteredGroups = createSelector(
  selectAllGroups,
  selectGroupsQuery,
  selectGroupsFilters,
  (groups, query, filters) => {
    const q = normalize(query);
    let result = q ? groups.filter((g) => normalize(g.groupName).includes(q)) : groups;

    for (const [key, values] of Object.entries(filters)) {
      if (!values.length) continue;
      result = result.filter((g) => {
        const rowVal = (g as unknown as Record<string, unknown>)[key];
        return values.some((v) => (Array.isArray(rowVal) ? rowVal.includes(v) : rowVal === v));
      });
    }

    return result;
  },
);

const getTenantNames = (
  a: (typeof selectAllAccounts extends (...args: any[]) => infer R ? R : never)[number],
): string[] =>
  (a.tenantAccounts ?? [])
    .map((ta) => ta.tenant?.tenantName)
    .filter((n): n is string => Boolean(n));

const getCategory = (
  a: (typeof selectAllAccounts extends (...args: any[]) => infer R ? R : never)[number],
): string => a.tenantAccounts?.[0]?.roles?.[0]?.accountCategory ?? 'USER';

const getGroupNames = (
  a: (typeof selectAllAccounts extends (...args: any[]) => infer R ? R : never)[number],
): string[] =>
  (a.tenantAccounts ?? [])
    .flatMap((ta) => ta.groups ?? [])
    .map((g) => g.accountName)
    .filter(Boolean);

const isBlocked = (
  a: (typeof selectAllAccounts extends (...args: any[]) => infer R ? R : never)[number],
): boolean => !!(a.accountSecurity?.blocked || a.tenantAccounts?.some((ta) => ta.blocked));

export const selectFilteredAccounts = createSelector(
  selectAllAccounts,
  selectAccountsQuery,
  selectAccountsFilters,
  (accounts, query, filters) => {
    let result = accounts;

    const q = normalize(query);
    if (q) {
      result = result.filter(
        (a) =>
          normalize(a.firstName).includes(q) ||
          normalize(a.lastName).includes(q) ||
          normalize(a.email).includes(q) ||
          normalize(a.accountName).includes(q) ||
          normalize(a.nickName).includes(q) ||
          normalize(a.phone).includes(q) ||
          getTenantNames(a).some((n) => normalize(n).includes(q)) ||
          getGroupNames(a).some((n) => normalize(n).includes(q)),
      );
    }

    for (const [key, values] of Object.entries(filters)) {
      if (!values.length) continue;
      result = result.filter((a) => {
        if (key === 'tenantNames')
          return values.some((v) => getTenantNames(a).includes(v as string));
        if (key === 'tenantUuids')
          return values.some((v) =>
            (a.tenantAccounts ?? []).some((ta) => ta.tenant?.tenantUuid === v),
          );
        if (key === 'category') return values.includes(getCategory(a));
        if (key === 'groupNames') return values.some((v) => getGroupNames(a).includes(v as string));
        if (key === 'status') return values.includes(isBlocked(a) ? 'blocked' : 'active');
        const rowVal = (a as unknown as Record<string, unknown>)[key];
        return values.some((v) => (Array.isArray(rowVal) ? rowVal.includes(v) : rowVal === v));
      });
    }

    return result;
  },
);
